import React, {useState} from 'react'
import {Box, IconButton, Skeleton, Stack, TablePagination, TextField, Tooltip, Typography} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import {useTranslation} from 'react-i18next'
import moment from 'moment'
import DashboardCard from '@containers/dashboard/ui/DashboardCard'
import {useDashboardSessionsQuery} from '@containers/dashboard/queryHooks/useDashboardQuery'
import {useDebounce} from '@containers/dashboard/helpers/useDebounce'
import {formatRelativeDate} from '@containers/dashboard/helpers/formatRelativeDate'

const SessionsCard = () => {
	const {t} = useTranslation()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search)

	const {data, refetch, isFetching, isLoading} = useDashboardSessionsQuery({
		start: page * rowsPerPage,
		length: rowsPerPage,
		search: debouncedSearch,
	})

	return (
		<DashboardCard
			title={t('dashboardPage.sessions')}
			action={<Tooltip title={t('update')}>
				<span>
					<IconButton size={'small'} onClick={() => refetch()} disabled={isFetching}><RefreshIcon fontSize={'small'}/></IconButton>
				</span>
			</Tooltip>}
		>
			<TextField
				size={'small'}
				fullWidth
				placeholder={t('dashboardPage.search')}
				value={search}
				onChange={(e) => {
					setSearch(e.target.value)
					setPage(0)
				}}
				sx={{mb: 1}}
			/>

			<Stack divider={<Box sx={{borderTop: 1, borderColor: 'divider'}}/>}>
				{isLoading && Array.from({length: 5}, (_, index) => (
					<Box key={index} sx={{py: .75}}>
						<Skeleton width={'60%'}/>
						<Skeleton width={'40%'} height={16}/>
					</Box>
				))}
				{(data?.list ?? []).map((item) => (
					<Box key={item.id} sx={{py: .75}}>
						<Stack direction={'row'} justifyContent={'space-between'} gap={1}>
							<Typography variant={'body2'} fontWeight={600} noWrap>{item.userName}</Typography>
							<Typography variant={'body2'} color={'text.secondary'} sx={{fontVariantNumeric: 'tabular-nums'}}>
								{item.userIP}
							</Typography>
						</Stack>
						<Typography variant={'caption'} color={'text.secondary'}>
							{formatRelativeDate(moment(item.start, 'YYYY/MM/DD HH:mm'), t, true) ?? item.start}
							{' · '}
							{item.duration}
						</Typography>
					</Box>
				))}
				{data && !data.list?.length && <Typography variant={'body2'} color={'text.secondary'} py={1}>
					{t('dashboardPage.empty')}
				</Typography>}
			</Stack>

			<TablePagination
				component={'div'}
				count={data?.iTotalDisplayRecords ?? 0}
				page={page}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={[10, 25, 50]}
				labelRowsPerPage={''}
				onPageChange={(_, value) => setPage(value)}
				onRowsPerPageChange={(e) => {
					setRowsPerPage(Number(e.target.value))
					setPage(0)
				}}
				sx={{'& .MuiTablePagination-toolbar': {px: 0}}}
			/>
		</DashboardCard>
	)
}

export default SessionsCard
